import React, { createContext, useContext, type ReactElement, type ReactNode } from 'react';

/**
 * Parameters handed to a {@link ProfileTriggerRenderer} at each trigger site.
 */
export type ProfileTriggerParams = {
	/** Atlassian Account ID of the person whose profile card should be shown. */
	userId: string;
	/** Cloud ID of the current tenant. */
	cloudId: string;
	/** The element that acts as the hover/click trigger (e.g. an avatar). */
	children: ReactElement;
};

/**
 * A render function that wraps `children` with a profile-card trigger.
 *
 * This is supplied by a provider that lives OUTSIDE the ai-mate render cluster
 * (e.g. the Rovo app shell / conversation-assistant-content-renderer) so that
 * `rovo-growth-pulse` and `rovo-platform-ui-components` never need to import
 * `@atlassian/user-profile-card` directly. Avoiding that direct package
 * dependency is what keeps these packages out of the package-level dependency
 * cycle enforced by the "No cyclic dependencies" ratchet.
 */
export type ProfileTriggerRenderer = (params: ProfileTriggerParams) => ReactElement;

const ProfileTriggerContext = createContext<ProfileTriggerRenderer | undefined>(undefined);

export type ProfileTriggerProviderProps = {
	/**
	 * The renderer used to wrap trigger sites. When omitted, consumers fall back
	 * to their existing (legacy) profile-card behaviour.
	 */
	renderProfileTrigger?: ProfileTriggerRenderer;
	children: ReactNode;
};

/**
 * Provides a {@link ProfileTriggerRenderer} to descendant trigger sites.
 *
 * Mount this once, high in the tree, from a package that is allowed to import
 * `@atlassian/user-profile-card`. Descendants read the renderer via
 * {@link useProfileTrigger}.
 */
/* eslint-disable-next-line @atlaskit/platform/one-value-export-per-file -- legacy onboarding baseline */
export const ProfileTriggerProvider = ({
	renderProfileTrigger,
	children,
}: ProfileTriggerProviderProps): ReactElement => (
	<ProfileTriggerContext.Provider value={renderProfileTrigger}>
		{children}
	</ProfileTriggerContext.Provider>
);

/**
 * Returns the injected {@link ProfileTriggerRenderer}, or `undefined` when no
 * provider is present. Callers MUST handle the `undefined` case by falling back
 * to their existing profile-card behaviour.
 */
/* eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports, @atlaskit/platform/one-value-export-per-file -- legacy onboarding baseline */
export const useProfileTrigger = (): ProfileTriggerRenderer | undefined =>
	useContext(ProfileTriggerContext);
